import { IS_ADMIN_MODE } from '../../../hooks/useIsAdminMode'
import type { StepUsage } from '../../../types'
import { formatCostMicros } from '../../../utils/usageFormat'

export function formatTokens(count: number): string {
  if (count < 1000) return count.toLocaleString()
  return `${(count / 1000).toFixed(count < 10000 ? 1 : 0)}k`
}

// Synthetic turns (e.g. the bulk-edit confirmation apply, which runs without a
// model call) tag their persisted message with a placeholder like
// "<confirmation>" so the meta slot isn't empty. No real cost or tokens to
// show, so the chip is hidden.
function isInternalSentinelModel(model: string | null | undefined): boolean {
  return (
    typeof model === 'string' && model.startsWith('<') && model.endsWith('>')
  )
}

interface UsageChipProps {
  usage: StepUsage
}

export default function UsageChip({ usage }: UsageChipProps) {
  if (isInternalSentinelModel(usage.model)) return null
  const cacheRead = usage.cacheReadInputTokens ?? 0
  const cacheCreate = usage.cacheCreationInputTokens ?? 0
  const isStep = usage.step >= 0
  const headerParts: string[] = []
  if (isStep) headerParts.push(`Step ${usage.step + 1}`)

  const tokenParts: string[] = []
  tokenParts.push(`${formatTokens(usage.inputTokens)} in`)
  tokenParts.push(`${formatTokens(usage.outputTokens)} out`)
  if (cacheRead > 0) tokenParts.push(`${formatTokens(cacheRead)} cached read`)
  if (cacheCreate > 0)
    tokenParts.push(`${formatTokens(cacheCreate)} cached write`)
  // Cost is a non-production affordance only.
  if (IS_ADMIN_MODE && usage.costMicros > 0)
    tokenParts.push(formatCostMicros(usage.costMicros))

  return (
    <div className="flex flex-col gap-0.5 pl-1 text-[11px] text-blue-6 tabular-nums">
      {headerParts.length > 0 && <p>{headerParts.join(' · ')}</p>}
      <p className="opacity-80">{tokenParts.join(' · ')}</p>
    </div>
  )
}
